/**
 * TrustAccountsReport — Brief 52 (14 Jul 2026).
 *
 * Firm-wide (director/COFA/MD) or own-matters (fee earner) view of every
 * open matter's client-account and office-account position side by side,
 * with three red-flag rules surfaced as coloured pills and a CSV export.
 *
 * Read-only surface over the Brief 52 API:
 *   GET /api/hub/finance/trust-accounts-report      → JSON envelope
 *   GET /api/hub/finance/trust-accounts-report.csv  → CSV download
 *
 * The server does all role gating and sorting — this page renders whatever
 * the API returns. No client-side sorting, no pagination.
 *
 * States mirror MoAHistoryTab (Brief 51.1): loading skeleton, error band +
 * retry, empty message, populated table. Conventions kept identical so the
 * finance surfaces read as a coherent set.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageHero from "../components/PageHero";
import axios from "axios";
import {
  RefreshCw, AlertCircle, Scale, Download, ChevronDown,
} from "lucide-react";
import { API } from "../config/api";

const EM_DASH = "—";

const FLAG_META = {
  aged_client_balance: {
    color: "bg-status-amber-dim/20 text-status-amber border-status-amber/30",
    label: (d) =>
      d && Number.isFinite(Number(d.days))
        ? `Aged — no movement in ${d.days}d`
        : "Aged client balance",
  },
  overdrawn: {
    color: "bg-status-red-dim/20 text-status-red border-status-red/30",
    label: (d) =>
      d && Number.isFinite(Number(d.amount))
        ? `Overdrawn ${fmtGbp(d.amount)}`
        : "Overdrawn",
  },
  mismatched_linked_pr: {
    color: "bg-status-amber-dim/20 text-status-amber border-status-amber/30",
    label: (d) =>
      d
        ? `Mismatched PR link (${d.sub_a || 0}, ${d.sub_b || 0})`
        : "Mismatched PR link",
  },
};

export function fmtGbp(raw) {
  if (raw === null || raw === undefined || raw === "") return EM_DASH;
  const n = Number(raw);
  if (!Number.isFinite(n)) return EM_DASH;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  return `${sign}£${abs.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function fmtDate(iso) {
  if (!iso) return EM_DASH;
  const d = new Date(iso);
  if (isNaN(d)) return EM_DASH;
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-GB", { month: "short" });
  return `${day} ${month} ${d.getFullYear()}`;
}

function _headers() {
  const email =
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") || "";
  return {
    "X-Bonsai-User-Id":
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") || email,
    "X-User-Email": email,
  };
}

function _qs(includeClosed, feeEarner) {
  const p = new URLSearchParams();
  if (includeClosed) p.set("include_closed", "true");
  if (feeEarner) p.set("fee_earner", feeEarner);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export default function TrustAccountsReport() {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [feeEarnerList, setFeeEarnerList] = useState([]);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [feeEarner, setFeeEarner] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isDirector = report?.view === "director";

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await axios.get(
        `${API}/hub/finance/trust-accounts-report${_qs(includeClosed, feeEarner)}`,
        { headers: _headers() },
      );
      setReport(res.data || null);
    } catch {
      setError("Couldn't load the Trust Accounts Report");
    } finally {
      setLoading(false);
    }
  }, [includeClosed, feeEarner]);

  useEffect(() => { load(); }, [load]);

  // Fee-earner dropdown is director-only; fetch the list once we know the view.
  useEffect(() => {
    if (report?.view !== "director") return;
    let cancelled = false;
    axios
      .get(`${API}/hub/staff/fee-earners`, { headers: _headers() })
      .then((res) => {
        if (!cancelled) {
          setFeeEarnerList(Array.isArray(res.data) ? res.data : []);
        }
      })
      .catch(() => { if (!cancelled) setFeeEarnerList([]); });
    return () => { cancelled = true; };
  }, [report?.view]);

  const exportCsv = () => {
    window.location.href =
      `${API}/hub/finance/trust-accounts-report.csv${_qs(includeClosed, feeEarner)}`;
  };

  const rows = report?.rows || [];
  const aggregate = report?.aggregate || null;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="trust-accounts-report">
      {/* Header band */}
      {/* 27 Oct 2026 system-wide refresh: header band. */}
      <PageHero icon={Scale} title="Trust Accounts" subtitle="Client & office positions across matters"
        tile="linear-gradient(135deg, #3b82f6, #8b5cf6)" testId="trust-accounts-hero">
        <button
          onClick={exportCsv}
          disabled={loading || !!error}
          className="cm-glass inline-flex items-center gap-1.5 text-white ring-1 ring-white/20 hover:ring-white/40 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg px-3 py-1.5 text-xs font-semibold"
          data-testid="trust-accounts-report-export"
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </PageHero>

      {/* Aggregate strip — director only */}
      {aggregate && (
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5"
          data-testid="trust-accounts-report-aggregate"
        >
          <AggTile label="Matters" value={aggregate.matter_count} />
          <AggTile label="Client balance" value={fmtGbp(aggregate.client_balance_total)} />
          <AggTile label="Office outstanding" value={fmtGbp(aggregate.office_outstanding_total)} />
          <FlagTile counts={aggregate.flag_counts} />
        </div>
      )}

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
          <input
            type="checkbox"
            checked={includeClosed}
            onChange={(e) => { setLoading(true); setIncludeClosed(e.target.checked); }}
            className="accent-teal"
            data-testid="trust-accounts-report-include-closed"
          />
          Include closed matters
        </label>

        {isDirector && (
          <div className="relative">
            <select
              value={feeEarner}
              onChange={(e) => { setLoading(true); setFeeEarner(e.target.value); }}
              className="appearance-none bg-navy-card border border-white/10 rounded-lg pl-3 pr-8 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-teal/50"
              data-testid="trust-accounts-report-fee-earner"
            >
              <option value="">All fee earners</option>
              {feeEarnerList.map((fe) => (
                <option key={fe.initials} value={fe.initials}>
                  {fe.initials}{fe.name ? ` — ${fe.name}` : ""}
                </option>
              ))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Body */}
      {loading ? (
        <div className="space-y-1.5" data-testid="trust-accounts-report-loading">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 bg-white/5 rounded animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div
          className="flex items-center gap-2 bg-status-red-dim/20 border border-status-red/30 rounded-lg px-3 py-2"
          data-testid="trust-accounts-report-error"
        >
          <AlertCircle className="w-4 h-4 text-status-red shrink-0" />
          <span className="text-xs text-status-red flex-1">{error}</span>
          <button
            onClick={() => { setLoading(true); load(); }}
            className="text-status-red hover:text-white shrink-0"
            aria-label="Retry"
            data-testid="trust-accounts-report-retry"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : rows.length === 0 ? (
        <p
          className="text-sm text-gray-500 py-16 text-center"
          data-testid="trust-accounts-report-empty"
        >
          No matters to show
        </p>
      ) : (
        <div className="overflow-x-auto bg-navy-card border border-white/10 rounded-xl">
          <table className="w-full text-xs" data-testid="trust-accounts-report-table">
            <thead>
              <tr className="text-slate-500 uppercase tracking-wider text-[10px] border-b border-white/10">
                <th className="text-left font-semibold px-3 py-2">Matter</th>
                <th className="text-left font-semibold px-3 py-2">Fee earner</th>
                <th className="text-right font-semibold px-3 py-2">Client balance</th>
                <th className="text-left font-semibold px-3 py-2">Last MoA movement</th>
                <th className="text-right font-semibold px-3 py-2">Office outstanding</th>
                <th className="text-left font-semibold px-3 py-2">Last office activity</th>
                <th className="text-left font-semibold px-3 py-2">Flags</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              {rows.map((r) => {
                const clientNeg = Number(r.client_balance) < 0;
                return (
                  <tr
                    key={r.matter_reference}
                    onClick={() => navigate(`/matters/${encodeURIComponent(r.matter_reference)}`)}
                    className="border-t border-white/5 hover:bg-white/5 cursor-pointer"
                    data-testid={`trust-accounts-report-row-${r.matter_reference}`}
                  >
                    <td className="px-3 py-2">
                      <div className="font-mono text-white">{r.matter_reference}</div>
                      {r.matter_name ? (
                        <div className="text-slate-500 truncate max-w-[220px]">{r.matter_name}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 font-mono">{r.fee_earner_initials || EM_DASH}</td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${clientNeg ? "text-status-red" : "text-white"}`}
                      data-testid={`trust-accounts-report-client-balance-${r.matter_reference}`}
                    >
                      {fmtGbp(r.client_balance)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-400">
                      {fmtDate(r.client_last_movement_at)}
                      {r.client_last_movement_type ? (
                        <span className="text-slate-600"> ({r.client_last_movement_type})</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-300">
                      {fmtGbp(r.office_outstanding)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-400">
                      {fmtDate(r.office_last_activity_at)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(r.flags || []).map((f) => {
                          const meta = FLAG_META[f]
                            || { color: "bg-white/5 text-slate-400 border-white/10", label: () => f };
                          const detail = (r.flag_details || {})[f];
                          const text = meta.label(detail);
                          return (
                            <span
                              key={f}
                              title={text}
                              className={`inline-block px-1.5 py-0.5 rounded-full border text-[10px] ${meta.color}`}
                              data-testid={`trust-accounts-report-flag-${r.matter_reference}-${f}`}
                            >
                              {text}
                            </span>
                          );
                        })}
                        {(r.flags || []).length === 0 ? (
                          <span className="text-slate-600">{EM_DASH}</span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AggTile({ label, value }) {
  return (
    <div className="bg-navy-card border border-white/10 rounded-xl px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-bold text-white mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function FlagTile({ counts }) {
  const c = counts || {};
  const total = Object.values(c).reduce((a, b) => a + (Number(b) || 0), 0);
  const hot = total > 0;
  return (
    <div
      className={`bg-navy-card border rounded-xl px-4 py-3 border-l-4 ${
        hot ? "border-status-red border-l-status-red" : "border-white/10 border-l-white/10"
      }`}
      data-testid="trust-accounts-report-flag-tile"
    >
      <div className="text-[10px] uppercase tracking-wider text-slate-500">Flags</div>
      <div className={`text-lg font-bold mt-1 tabular-nums ${hot ? "text-status-red" : "text-white"}`}>
        {total}
      </div>
    </div>
  );
}
